import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PropTypes } from 'prop-types';

// Components
import EventListItemRedesign from '@molecules/EventListItemRedesign';
import LoadingArea from '@molecules/LoadingArea';
import { BodyMedium, DivWithShadowAlternate, H4 } from '@styled';

// Functionality
import useRegisteredEvents from '../../../hooks/useRegisteredEvents';
import { mapGraphEventToProps } from '@general/transforms/event.transform';
import useEditPropertyEvents from '@hooks/useEditPropertyEvents';

const PropertyEventList = ({ propertyId, events: eventsInitial }) => {
    const [events, setEvents] = useState(eventsInitial);
    const [isUpdating, setIsUpdating] = useState(false);
    const { registeredEvents, error: registeredEventsError } = useRegisteredEvents();

    const [updatePropertyEvents] = useEditPropertyEvents({
        onCompleted: response => {
            setIsUpdating(false);
            setEvents(response.updateProperty.events.map(e => e.code));
        },
    });

    const addPropertyEvent = event => {
        if (isUpdating) {
            return;
        }

        setIsUpdating(true);
        updatePropertyEvents({ variables: { id: propertyId, events: [...events, event.code] } });
    };

    const removePropertyEvent = event => {
        if (isUpdating) {
            return;
        }

        const eventsInput = [...events];
        const eventIndex = eventsInput.indexOf(event.code);
        if (eventIndex >= 0) {
            eventsInput.splice(eventIndex, 1);
        }

        setIsUpdating(true);
        updatePropertyEvents({ variables: { id: propertyId, events: eventsInput } });
    };

    if (registeredEventsError) {
        throw registeredEventsError;
    }

    if (!registeredEvents) {
        return <LoadingArea minHeight={270} />;
    }

    const isAssociated = code => events.includes(code);

    return (
        <div>
            <H4 className="mb-1.5rem">Associated Events</H4>
            <DivWithShadowAlternate className="p-4">
                <BodyMedium>Would you like to associate this property with an upcoming event?</BodyMedium>
                <BodyMedium className="mb-1rem">
                    Explore our{' '}
                    <Link className="text-color-link" to="/search?type=events">
                        events
                    </Link>{' '}
                    and register today to promote your property to event attendees.
                </BodyMedium>

                {registeredEvents.length === 0 && <BodyMedium>No registered events.</BodyMedium>}
                {registeredEvents.length > 0 && (
                    <div
                        style={{
                            borderBottom: '1px solid #eceff1',
                        }}
                    >
                        {registeredEvents
                            .filter(item => item?.recordId)
                            .slice(0, 20)
                            .map(item => (
                                <EventListItemRedesign
                                    key={item.recordId}
                                    {...mapGraphEventToProps(item)}
                                    isLoggedIn
                                    isFavorite={false}
                                    showFavoriteIcon={false}
                                    chip={isAssociated(item.code) ? { color: 'green', text: 'Already Associated' } : {}}
                                    bottomLink={
                                        isAssociated(item.code)
                                            ? {
                                                  function: removePropertyEvent,
                                                  text: 'Remove my property from this event',
                                              }
                                            : { function: addPropertyEvent, text: 'Promote my property at this event' }
                                    }
                                />
                            ))}
                    </div>
                )}
            </DivWithShadowAlternate>
        </div>
    );
};

PropertyEventList.propTypes = {
    propertyId: PropTypes.string.isRequired,
    events: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PropertyEventList;
